import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase"; 
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import styles from "./InstaGrid.module.css";

// A small sub-component for the video to handle its own Intersection Observer
const GalleryVideo = ({ src }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                const video = videoRef.current;
                if (!video) return;

                if (entry.isIntersecting) {
                    // Force the video to start loading and playing
                    video.play().catch((err) => {
                        // This handles the "User hasn't interacted" browser error
                        console.log("Autoplay nudge required:", err);
                    });
                } else {
                    video.pause();
                }
            },
            { threshold: 0.1 }
        );

        if (videoRef.current) {
            observer.observe(videoRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <video 
            ref={videoRef}
            /* The #t=0.001 trick forces the browser to load 1 frame so it's not black */
            src={`${src}?v=1#t=0.001`} 
            muted 
            loop 
            playsInline 
            /* We remove autoPlay here and let the IntersectionObserver handle it via .play() */
            preload="metadata" 
            className={styles.videoElement}
            /* If it still won't play, this event listener acts as a backup */
            onLoadedMetadata={(e) => {
                if (e.target.paused) e.target.play().catch(() => {});
            }}
        />
    );
};

const InstaGrid = () => {
    const [mediaData, setMediaData] = useState([]);
    const [viewAll, setViewAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [columnsCount, setColumnsCount] = useState(3);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) setColumnsCount(1);
            else if (window.innerWidth <= 1100) setColumnsCount(2);
            else setColumnsCount(3);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        fetchMedia();
    }, []);

    const fetchMedia = async () => {
        try {
            setLoading(true);
            const galleryRef = collection(db, "gallery");
            const q = query(galleryRef, orderBy("order", "asc"));
            const snapshot = await getDocs(q);

            const formattedMedia = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    type: data.type?.toUpperCase() === "VIDEO" ? "VIDEO" : "IMAGE",
                    url: data.url,
                    order: Number(data.order)
                };
            });

            setMediaData(formattedMedia.sort((a, b) => a.order - b.order));
        } catch (error) {
            console.error("Error fetching media:", error.message);
        } finally {
            setLoading(false);
        }
    };

    const visibleMedia = viewAll ? mediaData : mediaData.slice(0, 12);
    const columns = Array.from({ length: columnsCount }, () => []);
    visibleMedia.forEach((item, index) => {
        columns[index % columnsCount].push(item);
    });

    if (loading) return <div className={styles.loader}>Loading Gallery...</div>;

    return (
        <section className={styles.wrapper}>
            <h2 className={styles.heading}>Gallery</h2>
            <div className={`${styles.masonryContainer} ${!viewAll ? styles.collapsed : ""}`}>
                <div className={styles.masonryGrid}>
                    {columns.map((col, colIdx) => (
                        <div key={colIdx} className={styles.column}>
                            {col.map((item) => (
                                <div key={item.id} className={styles.card}>
                                    {item.type === "IMAGE" ? (
                                        <img src={`${item.url}?v=1`} alt="Gallery Media" loading="lazy" />
                                    ) : (
                                        <GalleryVideo src={item.url} />
                                    )}
                                    <div className={styles.overlay}>
                                        {item.type === "VIDEO" && <span className={styles.videoIcon}>▶</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                {!viewAll && mediaData.length > 6 && <div className={styles.gradientFade}></div>}
            </div>
            {!viewAll && mediaData.length > columnsCount * 2 && (
                <div className={styles.viewAllDiv}>
                    <button className={styles.viewAll} onClick={() => setViewAll(true)}>
                        View All Gallery
                    </button>
                </div>
            )}
        </section>
    );
};

export default InstaGrid;